import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { periodStartingOn, periodWeeksEnd } from '../../../lib/build/request-periods.ts';
import { specialOverrides, type SpecialPick } from '../../../lib/build/special-overrides.ts';
import { addDays, mondayOnOrBefore, mondaysBetween } from '../../../lib/dates/iso.ts';
import { formatDayYear, formatPeriod, formatRange } from '../../../lib/format/dates.ts';
import { buildWeeks, DEFAULT_PERIOD_MONTHS, PERIOD_MONTHS, periodEnd, type PeriodMonths } from '../../../lib/weeks/weeks.ts';
import { ApiFailure, api, type HolidayCheckStatus } from '../../api.ts';
import { Notice } from '../../components/Notice.tsx';
import { copy, formatDateTime } from '../../copy/en-CA.ts';
import { FROM_ADMIN } from '../../nav.ts';
import { PendingRequestsNotice, type PeriodsState } from './PendingRequestsNotice.tsx';

const c = copy.admin.build;

/**
 * The quiet notice from the daily cross-check against canada-holidays.ca
 * (CLAUDE.md §5). Our rules decide the schedule; this only says whether a
 * second opinion agrees, so a disagreement is for the admin to look into,
 * never something the app resolves by itself.
 */
function HolidayCheckNotice() {
  const [status, setStatus] = useState<HolidayCheckStatus | null | 'unavailable'>(null);
  useEffect(() => {
    api.admin
      .holidayCheck()
      .then(setStatus)
      .catch(() => {
        setStatus('unavailable');
      });
  }, []);
  if (status === null) return null;
  if (status === 'unavailable') return <p className="muted small">{c.check.unavailable}</p>;
  const problems = status.years.filter((y) => !y.ok);
  if (problems.length > 0) {
    return (
      <Notice kind="info">
        <p>{c.check.disagree}</p>
        <ul className="plain">
          {problems.flatMap((y) => [
            ...y.disagreements.map((d) => (
              <li key={`${String(y.year)}-${d.name}-${d.what}`}>
                {d.what === 'date' ? c.check.date(d.name, formatDayYear(d.ours), formatDayYear(d.theirs)) : c.check.observed(d.name, formatDayYear(d.ours), formatDayYear(d.theirs))}
              </li>
            )),
            ...y.unknownToUs.map((h) => <li key={`${String(y.year)}-us-${h.name}`}>{c.check.unknownToUs(h.name, formatDayYear(h.date))}</li>),
            ...y.unknownToApi.map((h) => <li key={`${String(y.year)}-api-${h.name}`}>{c.check.unknownToApi(h.name, formatDayYear(h.date))}</li>),
          ])}
        </ul>
      </Notice>
    );
  }
  if (status.stale) {
    return (
      <Notice kind="info">
        {status.lastSuccessAt ? c.check.stale(formatDateTime(status.lastSuccessAt)) : c.check.never}
        {status.failure ? ` ${c.check.failure[status.failure] ?? ''}` : ''}
      </Notice>
    );
  }
  return <p className="muted small">{c.check.agree(formatDateTime(status.lastSuccessAt))}</p>;
}

/**
 * The period's length: a number of months, a custom end, or the dates of the
 * request period that starts on the chosen Monday ("Match …").
 */
type Length = PeriodMonths | 'custom' | 'period';

/**
 * Pick a period and generate a draft. The holidays in the period are
 * computed here with the same pure module the server uses, so what the
 * admin sees before generating is exactly what the generator will use.
 */
export function AdminBuildPage() {
  const navigate = useNavigate();
  const [suggested, setSuggested] = useState<string | null>(null);
  const [hasHistory, setHasHistory] = useState(false);
  const [drafts, setDrafts] = useState(0);
  const [physicianCount, setPhysicianCount] = useState<number | null>(null);
  const [civic, setCivic] = useState(true);
  const [start, setStart] = useState('');
  // The admin's choice of length; null until they make one, which means the
  // default: the matching request period when there is one, else 6 months.
  const [chosenLength, setChosenLength] = useState<Length | null>(null);
  const [customEnd, setCustomEnd] = useState('');
  const [periods, setPeriods] = useState<PeriodsState>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.schedules(),
      api.physicians(),
      api.settings(),
      // Loaded with the rest so the default length does not change under the
      // admin a moment later. If only these fail, the page still works and
      // says the pending requests could not be checked.
      api.requestPeriods().then(
        (r) => r.periods,
        () => 'unavailable' as const,
      ),
    ])
      .then(([s, p, settings, requestPeriods]) => {
        setSuggested(s.suggestedStart);
        setHasHistory(s.schedules.some((x) => x.status === 'saved'));
        setDrafts(s.schedules.filter((x) => x.status === 'draft').length);
        // A start the admin already typed while the page loaded is kept.
        setStart((typed) => typed || (s.suggestedStart ?? s.today));
        setPhysicianCount(p.physicians.length);
        setCivic(settings.civicHoliday);
        setPeriods(requestPeriods);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
      });
  }, []);

  const startMonday = start ? mondayOnOrBefore(start) : '';
  // The request period that starts on the chosen Monday, offered as a length.
  const match = periods === null || periods === 'unavailable' ? null : periodStartingOn(periods, startMonday);
  const matchEnd = match ? periodWeeksEnd(match.endDate) : '';
  // "Match" is the default whenever a period starts on the chosen Monday; if
  // the admin chose it and then moved the start, it falls back to 6 months.
  const wanted: Length = chosenLength ?? (match ? 'period' : DEFAULT_PERIOD_MONTHS);
  const length: Length = wanted === 'period' && !match ? DEFAULT_PERIOD_MONTHS : wanted;
  const end = useMemo(() => {
    if (!startMonday) return '';
    if (length === 'period') return matchEnd;
    // A custom end in mid-week means "through that week": the server widens
    // it to the week's Sunday, so show the same here.
    if (length === 'custom') return customEnd && customEnd >= startMonday ? addDays(mondayOnOrBefore(customEnd), 6) : customEnd;
    return periodEnd(startMonday, length);
  }, [startMonday, length, customEnd, matchEnd]);
  // A schedule over exactly a request period's dates takes that period's
  // name ("January to July 2027"), whichever length button produced them.
  const draftName = match && end === matchEnd ? match.name : startMonday && end ? formatPeriod(startMonday, end) : '';

  // The weeks by the rule alone, from which the Christmas/New Year pairs are found.
  const ruleWeeks = useMemo(() => (startMonday && end && end >= startMonday ? buildWeeks({ start: startMonday, end, options: { civicHoliday: civic } }) : []), [startMonday, end, civic]);
  // One pair per Christmas in the period: the rule's Christmas week and the week after it.
  const pairs = useMemo(() => ruleWeeks.filter((w) => w.kind === 'christmas').map((w) => ({ christmas: w.weekStart, newYear: addDays(w.weekStart, 7) })), [ruleWeeks]);
  // The admin's confirmation, keyed by the rule's Christmas week; absent means "as the rule says".
  const [chosen, setChosen] = useState<Record<string, SpecialPick>>({});
  // Only the weeks the admin changed from the rule's pick: an accepted pick
  // sends nothing, so no week claims to be "Special week set by admin".
  const overrides = useMemo(() => specialOverrides(pairs, chosen), [pairs, chosen]);
  const sameWeek = pairs.some((pair) => {
    const pick = chosen[pair.christmas] ?? pair;
    return pick.christmas !== null && pick.christmas === pick.newYear;
  });
  const weeks = useMemo(
    () => (startMonday && end && end >= startMonday ? buildWeeks({ start: startMonday, end, options: { civicHoliday: civic }, overrides }) : []),
    [startMonday, end, civic, overrides],
  );
  const notable = weeks.filter((w) => w.kind !== 'regular');
  /** The weeks an admin may name as Christmas or New Year: around the rule's pick. */
  const candidates = (pair: { christmas: string; newYear: string }) =>
    ruleWeeks.filter((w) => w.weekStart >= addDays(pair.christmas, -21) && w.weekStart <= addDays(pair.newYear, 21));

  const setCivicSetting = async (on: boolean) => {
    setCivic(on);
    try {
      await api.admin.setSettings({ civicHoliday: on });
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    }
  };

  const generate = async () => {
    setError(null);
    setBusy(true);
    try {
      const { schedule } = await api.admin.buildSchedule({ start: startMonday, end, name: draftName, ...(Object.keys(overrides).length > 0 ? { overrides } : {}) });
      await navigate(`/schedules/${schedule.schedule.id}`, { state: FROM_ADMIN });
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    } finally {
      setBusy(false);
    }
  };

  const canGenerate = weeks.length > 0 && (physicianCount ?? 0) >= 2 && !busy && !sameWeek;

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{hasHistory && suggested ? c.intro(formatDayYear(suggested)) : c.introNoHistory}</p>
      </div>

      {drafts > 0 ? <Notice kind="info">{c.draftsExist(drafts)}</Notice> : null}
      {physicianCount !== null && physicianCount < 2 ? <Notice kind="error">{c.needRoster}</Notice> : null}

      <section className="card" aria-labelledby="step-period">
        <h2 id="step-period">
          <span className="step">1</span> {c.step1}
        </h2>
        <div className="row" role="group" aria-label={c.length}>
          {match ? (
            <button type="button" className={`seg match ${length === 'period' ? 'on' : ''}`} aria-pressed={length === 'period'} onClick={() => { setChosenLength('period'); }}>
              {c.matchPeriod(match.name, formatPeriod(match.startDate, matchEnd), mondaysBetween(match.startDate, matchEnd).length)}
            </button>
          ) : null}
          {PERIOD_MONTHS.map((m) => (
            <button key={m} type="button" className={`seg ${length === m ? 'on' : ''}`} aria-pressed={length === m} onClick={() => { setChosenLength(m); }}>
              {c.months(m)}
            </button>
          ))}
          <button type="button" className={`seg ${length === 'custom' ? 'on' : ''}`} aria-pressed={length === 'custom'} onClick={() => { setChosenLength('custom'); }}>
            {c.custom}
          </button>
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="build-start">{c.start}</label>
            <input id="build-start" className="input" type="date" value={start} onChange={(e) => { setStart(e.target.value); }} />
          </div>
          {length === 'custom' ? (
            <div className="field">
              <label htmlFor="build-end">{c.end}</label>
              <input id="build-end" className="input" type="date" value={customEnd} onChange={(e) => { setCustomEnd(e.target.value); }} />
            </div>
          ) : null}
          <div className="field">
            <span className="label-like">{c.result}</span>
            <span className="big">{weeks.length > 0 ? c.resultLine(formatPeriod(startMonday, end), weeks.length) : '–'}</span>
          </div>
        </div>
      </section>

      <section className="card" aria-labelledby="step-holidays">
        <h2 id="step-holidays">
          <span className="step">2</span> {c.step2}
        </h2>
        {notable.length === 0 ? <p className="muted">{c.noHolidays}</p> : null}
        {notable.length > 0 ? (
          <ul className="holiday-list">
            {notable.map((w) => (
              <li key={w.weekStart}>
                <span className="range">{formatRange(w.onCallStart, w.onCallEnd)}</span>
                {w.kind === 'long-weekend' ? (
                  <span className="badge lw">{c.effects['long-weekend'](w.longWeekendHolidays.join(', '), w.days)}</span>
                ) : w.kind === 'christmas' ? (
                  <span className="badge xmas">{c.effects.christmas}</span>
                ) : (
                  <span className="badge ny">{c.effects['new-year']}</span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        <label className="checkbox">
          <input type="checkbox" checked={civic} onChange={(e) => void setCivicSetting(e.target.checked)} />
          {c.civic}
        </label>
        <p className="muted small">{c.civicNote}</p>
        <HolidayCheckNotice />
        {pairs.length > 0 ? (
          <div className="stack special-confirm" role="group" aria-labelledby="special-h">
            <h3 id="special-h">{c.special}</h3>
            <p className="muted small">{c.specialIntro}</p>
            {pairs.map((pair) => {
              const pick = chosen[pair.christmas] ?? pair;
              const options = candidates(pair);
              const select = (which: 'christmas' | 'newYear', id: string, label: string) => (
                <div className="field">
                  <label htmlFor={id}>{label}</label>
                  <select
                    id={id}
                    className="input"
                    value={pick[which] ?? ''}
                    onChange={(e) => {
                      const value = e.target.value || null;
                      setChosen((prev) => ({ ...prev, [pair.christmas]: { ...pick, [which]: value } }));
                    }}
                  >
                    <option value="">{c.noSpecial}</option>
                    {options.map((w) => (
                      <option key={w.weekStart} value={w.weekStart}>
                        {formatRange(w.weekStart, addDays(w.weekStart, 6))}
                      </option>
                    ))}
                  </select>
                  {pick[which] !== pair[which] ? <span className="muted small">{c.specialAdjusted}</span> : null}
                </div>
              );
              return (
                <div className="row" key={pair.christmas}>
                  {select('christmas', `xmas-${pair.christmas}`, c.christmasWeek)}
                  {select('newYear', `ny-${pair.christmas}`, c.newYearWeek)}
                </div>
              );
            })}
            {sameWeek ? <Notice kind="error">{c.sameWeek}</Notice> : null}
          </div>
        ) : null}
      </section>

      <section className="card" aria-labelledby="step-generate">
        <h2 id="step-generate">
          <span className="step">3</span> {c.step3}
        </h2>
        <p className="muted">{c.generateNote}</p>
        <PendingRequestsNotice periods={periods} start={startMonday} end={end} />
        {error ? <Notice kind="error">{error}</Notice> : null}
        <div>
          <button type="button" className="btn primary" disabled={!canGenerate} onClick={() => void generate()}>
            {busy ? c.generating : c.generate}
          </button>
        </div>
      </section>
    </>
  );
}
